// ============================================================
// NavDrawer.jsx
// The mobile face of the primary navigation: the same <SideNav />,
// presented as an off-canvas sheet.
//
// Built on the native <dialog> + showModal(), the same primitive the
// shared Modal uses, because it hands us the load-bearing behaviour
// for free rather than hand-rolled:
//   - focus trap while open
//   - Escape to close
//   - focus returned to the hamburger that opened it
//   - a ::backdrop scrim
//   - the top layer, which escapes any ancestor overflow/transform
//     containing block (see Gotcha #85 — a position:fixed panel
//     inside a scroll container is exactly the trap this avoids)
//
// What this adds on top of native:
//   - React state sync between the `open` prop and the imperative
//     showModal() / close() calls
//   - backdrop-click-to-close, which native <dialog> does NOT do
//   - forwarding the native 'close' event (fired on Escape) through
//     onClose so React state stays consistent
//
// The drawer is only ever mounted below the shell breakpoint —
// AppShell renders either this or the desktop rail, never both — so
// the navigation exists in the DOM exactly once.
// ============================================================

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import SideNav from './SideNav';
import './NavDrawer.css';

export default function NavDrawer({ open, onClose, badges }) {
  const dialogRef = useRef(null);

  // Sync the `open` prop → the dialog's imperative API. showModal() is
  // what enables the focus trap and the ::backdrop scrim; setting
  // .open = true would NOT, so we go through the methods.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Escape closes the dialog directly through showModal()'s built-in
  // handler, bypassing our state. Forwarding the resulting 'close'
  // event keeps React in sync. The cycle when React drives the close
  // is benign and idempotent.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handler = () => onClose();
    dialog.addEventListener('close', handler);
    return () => dialog.removeEventListener('close', handler);
  }, [onClose]);

  // The dialog element fills the viewport; the visible sheet is
  // .nav-drawer__sheet inside it. A click landing on the dialog
  // itself is therefore a backdrop click.
  function handleBackdropClick(e) {
    if (e.target === dialogRef.current) onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      className="nav-drawer"
      onClick={handleBackdropClick}
      aria-label="Navigation"
    >
      <div className="nav-drawer__sheet">
        <button
          type="button"
          className="nav-drawer__close"
          onClick={onClose}
          aria-label="Close navigation"
        >
          <X size={20} />
        </button>
        {/* onNavigate closes the drawer when a destination is chosen —
            without it the sheet would sit over the page it just
            navigated to. */}
        <SideNav badges={badges} onNavigate={onClose} />
      </div>
    </dialog>
  );
}
